import * as React from 'react';
import {
  FormGroup,
  FormGroupFeedback,
  IDateInputProps,
  IFormGroupProps,
  Intent,
  TimePicker,
} from '@pinpointhq/thumbtack';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField } from 'formik';
import { format, parse } from 'date-fns';

interface ITimeField extends IDateInputProps {
  name: string;
  formGroupProps?: Omit<IFormGroupProps, 'labelFor'>;
  showArrowButtons?: boolean;
}

export default function TimeField<T extends string>({
  name,
  formGroupProps,
  showArrowButtons = true,
  ...timePickerProps
}: ITimeField) {
  const [field, meta, helpers]: [FieldInputProps<T>, FieldMetaProps<T>, FieldHelperProps<T>] = useField<T>(name);
  const hasError = meta.touched && meta.error;

  // The timepicker won't accept a null value. If you give it one it sets itself to 12:00 AM, but it doesn't set the
  // field value to that. So it looks like its been completed, but if you try and submit the form it won't actually
  // submit the attribute since formik doesn't think its been changed
  React.useEffect(() => {
    if (!field.value) {
      helpers.setValue(format(new Date(), 'YYYY-MM-DD HH:mm') as T);
    }
  }, []);

  const value = field.value ? parse(field.value) : undefined;

  function handleChange(selectedTime: Date) {
    helpers.setValue(format(selectedTime, 'YYYY-MM-DD HH:mm') as T);
  }

  return (
    <FormGroup {...formGroupProps} labelFor={name}>
      <TimePicker
        {...timePickerProps}
        useAmPm={true}
        showArrowButtons={showArrowButtons}
        value={value}
        onChange={handleChange}
      />
      {hasError && <FormGroupFeedback intent={Intent.DANGER}>{meta.error}</FormGroupFeedback>}
    </FormGroup>
  );
}
